import type { PerspectiveCamera, WebGLRenderer } from 'three';
import { fitView, toRadians, type ViewRect } from './scene-math';

/**
 * The logical 16:9 view (GDD §5.2, §15.8, issue #65): the canvas is the largest 16:9 rectangle that fits
 * its container, centred; the bands are the background of the page. The rectangle is published as CSS
 * custom properties so that the interface (#ui) is anchored to the view and not to the window.
 */
export interface ViewInfo {
  camera: {
    fov: number;
    aspect: number;
    position: { x: number; y: number; z: number };
    /** Unit vector the camera looks along. */
    direction: { x: number; y: number; z: number };
  };
  visibleHeightAtZ0: number;
  visibleWidthAtZ0: number;
  canvasRect: ViewRect;
  pixelRatio: number;
}

export interface View {
  rect(): ViewRect;
  info(): ViewInfo;
  dispose(): void;
}

export function attachView(container: HTMLElement, renderer: WebGLRenderer, camera: PerspectiveCamera): View {
  camera.aspect = 16 / 9;
  camera.updateProjectionMatrix();
  let current: ViewRect = { x: 0, y: 0, width: 0, height: 0 };

  const resize = () => {
    current = fitView(container.clientWidth, container.clientHeight);
    renderer.setSize(Math.max(1, current.width), Math.max(1, current.height), false);
    const style = renderer.domElement.style;
    style.position = 'absolute';
    style.left = `${current.x}px`;
    style.top = `${current.y}px`;
    style.width = `${current.width}px`;
    style.height = `${current.height}px`;
    const root = document.documentElement.style;
    root.setProperty('--nv-view-x', `${current.x}px`);
    root.setProperty('--nv-view-y', `${current.y}px`);
    root.setProperty('--nv-view-w', `${current.width}px`);
    root.setProperty('--nv-view-h', `${current.height}px`);
  };
  const observer = new ResizeObserver(resize);
  observer.observe(container);
  resize();

  return {
    rect: () => current,
    info() {
      const height = 2 * camera.position.z * Math.tan(toRadians(camera.fov / 2));
      const direction = camera.getWorldDirection(camera.position.clone());
      return {
        camera: {
          fov: camera.fov,
          aspect: camera.aspect,
          position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
          direction: { x: direction.x, y: direction.y, z: direction.z },
        },
        visibleHeightAtZ0: height,
        visibleWidthAtZ0: height * camera.aspect,
        canvasRect: { ...current },
        pixelRatio: renderer.getPixelRatio(),
      };
    },
    dispose() {
      observer.disconnect();
    },
  };
}
